import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  SimpleChange,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { NgxResponsiveTableDirective } from './ngx-responsive-table.directive';
import { BehaviorSubject } from 'rxjs';
import { NgxResponsiveTableService } from './ngx-responsive-table.service';
import { ToggleSwitchComponent } from './components/toggle-switch/toggle-switch.component';
import { NgxResponsiveTableConfig } from './models/ngx-responsive-table-config';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ngx-responsive-table',
  templateUrl: './ngx-responsive-table.component.html',
  styleUrls: [
    './ngx-responsive-table.component.scss',
    './mobile.scss',
    './freeze-first-column.scss',
    './density.scss',
  ],
})
export class NgxResponsiveTableComponent implements OnInit, OnChanges, AfterContentInit, OnDestroy {
  @Input() public tableConfig: NgxResponsiveTableConfig;
  @Input() public tableData: any;

  @ContentChildren(NgxResponsiveTableDirective, { descendants: true, read: ElementRef })
  public dataTableElementRefs: QueryList<ElementRef>;

  public config: NgxResponsiveTableConfig;
  public tableDensity: string;

  private contentInit = false;
  private elementRefReadDelay = 0;
  private contentRenderDelay = 0;
  private columnTitlesSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private toggleSwitchSubscriptions: Subscription[] = [];

  constructor(private viewContainerRef: ViewContainerRef,
              private renderer: Renderer2,
              private ngxResponsiveTableService: NgxResponsiveTableService) {
    this.config = new NgxResponsiveTableConfig();
  }

  public ngOnInit() {
  }

  public ngOnDestroy() {
    this.destroyToggleSwitchSubscriptions();
  }

  private destroyToggleSwitchSubscriptions() {
    if (!this.toggleSwitchSubscriptions || !this.toggleSwitchSubscriptions.length) {
      return;
    }

    this.toggleSwitchSubscriptions.forEach(
      (subscription) => {
        if (!subscription) {
          return true;
        }

        subscription.unsubscribe();
      },
    );

    this.toggleSwitchSubscriptions.fill(null);
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.onTableConfigChanged(changes['tableConfig']);
    this.onTableDataChanged(changes['tableData']);
  }

  private onTableConfigChanged(change: SimpleChange) {
    if (!change || !change.currentValue) {
      return;
    }

    if (!this.config) {
      this.config = new NgxResponsiveTableConfig();
    }

    Object.assign(this.config, change.currentValue);
  }

  private onTableDataChanged(change: SimpleChange) {
    if (!change || !change.currentValue || !this.contentInit) {
      return;
    }

    this.prepareTables();
  }

  public ngAfterContentInit() {
    this.prepareTables();
    this.contentInit = true;
  }

  private prepareTables() {
    if (!this.dataTableElementRefs) {
      return;
    }

    const tableElementRefs = this.dataTableElementRefs.map((elRef: ElementRef) => elRef.nativeElement);
    tableElementRefs.forEach(this.generateColumnTitles.bind(this));

    setTimeout(
      () => {
        tableElementRefs.forEach(this.collapseAllRows.bind(this));
      },
      this.contentRenderDelay);

    this.columnTitlesSubject.subscribe(
      (titles) => {
        if (!titles || !titles.length) {
          return;
        }

        for (let i = 0; i < titles.length; i++) {
          tableElementRefs.forEach(tableElement => this.setColumnTitles(tableElement, i, titles[i]));
        }
      },
    );
  }

  private generateColumnTitles(tableElement: any) {
    if (!tableElement) {
      return;
    }

    const headers = Array.from(tableElement.querySelectorAll('thead tr th'));
    setTimeout(
      () => {
        this.columnTitlesSubject.next(headers.map((header: any) => header.innerText));
      },
      this.elementRefReadDelay);
  }

  private setColumnTitles(tableElement: any, columnIndex: number, title: string) {
    if (!tableElement) {
      return;
    }

    const dataCells = Array.from(tableElement.querySelectorAll(`tbody tr td:nth-child(${columnIndex + 1})`));
    setTimeout(
      () => {
        dataCells.forEach(
          (dataCell: any) => {
            dataCell.setAttribute('data-title', title);
          },
        );
      },
      this.elementRefReadDelay);
  }

  private collapseAllRows(tableElement: any): void {
    if (!tableElement) {
      return;
    }

    this.destroyToggleSwitchSubscriptions();
    const dataRows = Array.from(tableElement.querySelectorAll(`tbody tr`));
    setTimeout(
      () => {
        dataRows.forEach(
          (dataRow: any) => {
            // Collapse a row by default.
            dataRow.classList.add('collapsed');

            // Dynamically append a toggle switch component to a row.
            const componentInstance = this.ngxResponsiveTableService.getComponentInstance(this.viewContainerRef, ToggleSwitchComponent);
            this.renderer.appendChild(dataRow, componentInstance.location.nativeElement);

            // Listen to the toggle switch's EventEmitter and add/remove a class accordingly.
            const toggleSwitchSubscription = componentInstance.instance.switchToggled.subscribe(
              (isOn) => {
                if (isOn) {
                  dataRow.classList.remove('collapsed');
                  return;
                }

                dataRow.classList.add('collapsed');
              },
            );

            this.toggleSwitchSubscriptions.push(toggleSwitchSubscription);
            this.setDefaultCollapsedOrder(dataRow);
          },
        );
      },
      this.elementRefReadDelay);
  }

  /**
   * Set default collapsed-order if none exists in a row.
   * @param dataRow
   */
  private setDefaultCollapsedOrder(dataRow: any): void {
    const dataCells = Array.from(dataRow.querySelectorAll('td'));
    const collapsedOrderDataCells: any[] = Array.from(dataRow.querySelectorAll(`td[collapsed-order]`));
    setTimeout(
      () => {
        if (!!collapsedOrderDataCells.length) {
          this.removeExceedingCollapsedOrders(collapsedOrderDataCells);
          return;
        }

        // Set default collapsed-order by the order it appears.
        let order = 1;
        dataCells.forEach(
          (dataCell: any) => {
            if (order > this.config.small.maxCollapsedColumnCount) {
              return false;
            }

            dataCell.setAttribute('collapsed-order', order++);
          },
        );
      },
      this.elementRefReadDelay);
  }

  private removeExceedingCollapsedOrders(dataCells: any[]): void {
    if (!dataCells || !dataCells.length || dataCells.length <= this.config.small.maxCollapsedColumnCount) {
      return;
    }

    // Sort cells by collapsed-order values.
    dataCells.sort(
      (cell1, cell2) => {
        const order1 = cell1.getAttribute('collapsed-order');
        const order2 = cell2.getAttribute('collapsed-order');

        return order1 - order2;
      },
    );

    for (let i = this.config.small.maxCollapsedColumnCount; i < dataCells.length; i++) {
      dataCells[i].removeAttribute('collapsed-order');
    }
  }

  public onDensityChanged(density: string) {
    this.tableDensity = density;
  }
}
